import { $color } from "@/utils/cssVarUtils"
import { checkboxAnatomy as parts } from "@chakra-ui/anatomy"
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleFunction,
  SystemStyleObject,
} from "@chakra-ui/theme-tools"

const baseStyleControl: SystemStyleFunction = (props) => {
  const colors = {
    borderNormal:  $color('scene-border'),
    inputBg:  $color('bg-input-normal'),
    textNormal:  $color('text-normal'),
    primaryNormal:  $color('scene-primary-normal'),
    lightTextNormal:  $color('light-text-normal'),
    primaryActive:  $color('scene-primary-active'),
    borderError:  $color('scene-danger-normal'),
  }

  return {
    w: "100%",
    border: '1px solid',
    borderColor: colors.borderNormal,
    color: colors.lightTextNormal,
    bg: colors.inputBg,
    borderRadius: "0.25em",
    transitionProperty: "background, border",
    transitionDuration: "normal",

    _checked: {
      bg: colors.primaryNormal,
      borderColor: 'transparent',

      _hover: {
        bg: colors.primaryActive,
        borderColor: '',
      },

      _disabled: {
        borderColor: '',
        bg: colors.primaryNormal,
        color: '',
        opacity: 0.4,
      },
    },

    _indeterminate: {
      bg: colors.primaryNormal,
      borderColor: 'transparent',
    },

    _disabled: {
      bg: '',
      borderColor: '',
      opacity: 0.4,
    },

    _focusVisible: {
      boxShadow: "none",
    },

    _invalid: {
      borderWidth: '1px',
      borderColor: colors.borderError,
    },
  }
}

const baseStyleContainer: SystemStyleObject = {
  _disabled: { cursor: "not-allowed" },
}

const baseStyleLabel: SystemStyleObject = {
  userSelect: "none",
  _disabled: { opacity: 0.4 },
}

const baseStyleIcon: SystemStyleObject = {
  transitionProperty: "transform",
  transitionDuration: "normal",
}

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  icon: baseStyleIcon,
  container: baseStyleContainer,
  control: baseStyleControl(props),
  label: baseStyleLabel,
})

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  xs: {
    container: {
      fontSize: "xs",
    },
    control: {
      h: 'md',
      w: 'md',
    },
    label: {
      fontSize: "xs",
    },
    icon: {
      fontSize: '2xs',
    },
  },
  sm: {
    container: {
      fontSize: "sm",
    },
    control: {
      h: '2xl',
      width: '2xl',
    },
    label: {
      fontSize: "sm",
    },
    icon: {
      fontSize: 'xs',
    },
  },
  md: {
    container: {
      fontSize: "sm",
    },
    control: {
      w: '4xl',
      h: '4xl',
    },
    label: {
      fontSize: "sm",
    },
    icon: {
      fontSize: 'md',
    },
  },
  lg: {
    container: {
      fontSize: "sm"
    },
    control: {
      w: '5xl',
      h: '5xl',
    },
    label: {
      fontSize: "sm",
    },
    icon: {
      fontSize: 'xl',
    },
  },
}

const defaultProps = {
  size: "xs",
}

export const Checkbox = {
  parts: parts.keys,
  baseStyle,
  sizes,
  defaultProps,
}